import {
  ColumnDataTypes,
  FilterOperators,
  FilterOptionTypes,
  type ColumnConfig,
  type FilterOperatorObject,
  type FilterOption,
  type FilterOptionsParams,
} from '@/components/DataTable';
import { Badge } from '@/components/ui/badge';
import type {
  AggregatedPackage,
  AggregatedRepository,
  AgentCommandEventType,
  AgentCommandFilterOptionField,
} from '@kirin/api-client/contracts/agent-command-events';
import {
  agentCommandFilterOptionsQueryKey,
  fetchAgentCommandEventFilterOptions,
} from './api';
import {
  AgentFilterOption,
  AgentsCell,
  CountButton,
  ProjectCell,
} from './CellRenderers';
import { OsvVulnerabilityCell } from './OsvVulnerabilityCell';

const DASH = '—';
const FILTER_OPTIONS_PAGE_SIZE = 10;

function text(value: string | null): string {
  return value && value.trim() ? value : DASH;
}

function formatTimestamp(value: string): string {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
}

// Multi-select distinct-value dropdown (chips) + free-text contains — the
// Projects/Inventory string-filter behavior. Backend `string` field type maps
// INCLUDES/EXCLUDES to exact `lower() IN (...)` and CONTAINS to ILIKE.
const STRING_OPERATORS: FilterOperatorObject[] = [
  { value: FilterOperators.INCLUDES, label: 'Includes' },
  { value: FilterOperators.EXCLUDES, label: 'Excludes' },
  { value: FilterOperators.CONTAINS, label: 'Contains' },
  { value: FilterOperators.NOT_CONTAINS, label: 'Does not contain' },
];

// Date range operators for the Last-seen column (backend `date` filter).
const DATE_OPERATORS: FilterOperatorObject[] = [
  { value: FilterOperators.GREATER_THAN, label: 'Is after' },
  { value: FilterOperators.LESS_THAN, label: 'Is before' },
  { value: FilterOperators.BETWEEN, label: 'Is between' },
];

const VALUELESS_OPERATORS: FilterOperatorObject[] = [
  { value: FilterOperators.IS_EMPTY, label: 'Is empty' },
  { value: FilterOperators.IS_NOT_EMPTY, label: 'Is not empty' },
];

// Numeric range operators for the aggregated Users / Occurrences count columns
// (backend `number` HAVING filter) — mirrors the alerts occurrenceCount column.
const NUMBER_OPERATORS: FilterOperatorObject[] = [
  { value: FilterOperators.INCLUDES, label: 'Includes' },
  { value: FilterOperators.EXCLUDES, label: 'Excludes' },
  ...VALUELESS_OPERATORS,
];

/**
 * Shared string-column filter config: a server-backed distinct-value multi-select
 * dropdown scoped to the current org (or the personal account when
 * `organizationId` is undefined).
 */
function stringFilter(
  field: AgentCommandFilterOptionField,
  organizationId: string | undefined,
  eventType: AgentCommandEventType
) {
  return {
    filterable: true as const,
    dataType: ColumnDataTypes.STRING,
    filterOperators: STRING_OPERATORS,
    filterOptions: {
      type: FilterOptionTypes.DYNAMIC,
      getQueryKey: (params: FilterOptionsParams) =>
        agentCommandFilterOptionsQueryKey(
          organizationId,
          eventType,
          field,
          params
        ),
      queryFn: (params: FilterOptionsParams) =>
        fetchAgentCommandEventFilterOptions(
          organizationId,
          eventType,
          field,
          params
        ),
      queryConfig: { staleTime: 2 * 60 * 1000 },
      pagination: { mode: 'offset' as const, limit: FILTER_OPTIONS_PAGE_SIZE },
    },
  };
}

export interface AggregatedPackageColumnActions {
  /** Open the unaggregated occurrences list for a group. */
  onShowOccurrences: (row: AggregatedPackage) => void;
}

/**
 * Columns for the aggregated Packages view (KIR-3807): one row per
 * package+source+project+version. Agent is a multi-chip cell; Users and
 * Occurrences are sortable/filterable counts; Vulnerabilities is a count that
 * opens the vuln list (not sortable — enriched post-query). Default sort is
 * last-seen descending (set on the DataTable, see index.tsx).
 */
export function createAggregatedPackageColumns(
  organizationId: string | undefined,
  actions: AggregatedPackageColumnActions
): ColumnConfig<AggregatedPackage>[] {
  const eventType: AgentCommandEventType = 'package';
  const agentFilter = stringFilter('agentName', organizationId, eventType);
  return [
    {
      id: 'packageName',
      header: 'Package',
      accessor: 'packageName',
      width: 220,
      renderCell: ({ row }) => (
        <div className="flex items-center gap-2">
          <span className="font-medium">{text(row.packageName)}</span>
          {row.knownBad && (
            <Badge variant="destructive" className="uppercase">
              Known bad
            </Badge>
          )}
        </div>
      ),
      sortable: true,
      sortField: 'packageName',
      ...stringFilter('packageName', organizationId, eventType),
    },
    {
      id: 'packageVersion',
      header: 'Version',
      accessor: 'packageVersion',
      width: 120,
      renderCell: ({ row }) => <span>{text(row.packageVersion)}</span>,
      sortable: true,
      sortField: 'packageVersion',
      ...stringFilter('packageVersion', organizationId, eventType),
    },
    {
      // KIR-3805: rich OSV cell, right after Version, sortable by severity
      // (Malicious → Critical → … → none). Not filterable.
      id: 'severity',
      header: 'Vulnerabilities (OSV)',
      accessor: 'osv',
      width: 260,
      renderCell: ({ row }) => <OsvVulnerabilityCell row={row} />,
      sortable: true,
      sortField: 'severity',
    },
    {
      id: 'packageManager',
      header: 'Manager',
      accessor: 'packageManager',
      width: 110,
      renderCell: ({ row }) => <span>{text(row.packageManager)}</span>,
      sortable: true,
      sortField: 'packageManager',
      ...stringFilter('packageManager', organizationId, eventType),
    },
    {
      id: 'sourceUrl',
      header: 'Source',
      accessor: 'sourceUrl',
      width: 220,
      renderCell: ({ row }) => (
        <span className="block truncate">{text(row.sourceUrl)}</span>
      ),
      sortable: true,
      sortField: 'sourceUrl',
      ...stringFilter('sourceUrl', organizationId, eventType),
    },
    {
      // Multi-chip Agent column. Backend filter field is `agents` (array
      // overlap); the dropdown reuses the distinct agentName options endpoint.
      id: 'agents',
      header: 'Agent',
      accessor: 'agents',
      width: 180,
      renderCell: ({ row }) => <AgentsCell agents={row.agents} />,
      filterable: true,
      dataType: ColumnDataTypes.ENUM,
      filterOperators: [
        { value: FilterOperators.INCLUDES, label: 'Includes' },
        { value: FilterOperators.EXCLUDES, label: 'Excludes' },
      ],
      filterOptions: {
        ...agentFilter.filterOptions,
        render: (option: FilterOption, isSelected: boolean) => (
          <AgentFilterOption value={option.value} isSelected={isSelected} />
        ),
      },
    },
    {
      id: 'userCount',
      header: 'Users',
      accessor: 'userCount',
      width: 100,
      renderCell: ({ row }) => <span>{row.userCount}</span>,
      sortable: true,
      sortField: 'userCount',
      filterable: true,
      dataType: ColumnDataTypes.NUMBER,
      filterOperators: NUMBER_OPERATORS,
      filterOptions: { type: FilterOptionTypes.STATIC, options: [] },
      range: { min: 1, max: 100, step: 1 },
    },
    {
      id: 'occurrenceCount',
      header: 'Occurrences',
      accessor: 'occurrenceCount',
      // Wide enough for the header + sort + filter icons (otherwise the filter
      // funnel crowds the next column, KIR-3807).
      width: 150,
      renderCell: ({ row }) => (
        <CountButton
          count={row.occurrenceCount}
          label={`Show ${row.occurrenceCount} occurrences of ${row.packageName ?? 'package'}`}
          onClick={() => actions.onShowOccurrences(row)}
        />
      ),
      sortable: true,
      sortField: 'occurrenceCount',
      filterable: true,
      dataType: ColumnDataTypes.NUMBER,
      filterOperators: NUMBER_OPERATORS,
      filterOptions: { type: FilterOptionTypes.STATIC, options: [] },
      range: { min: 1, max: 100, step: 1 },
    },
    {
      id: 'projectPath',
      header: 'Project',
      accessor: 'projectPath',
      width: 180,
      renderCell: ({ row }) => (
        <ProjectCell row={row} organizationId={organizationId} />
      ),
      sortable: true,
      sortField: 'projectPath',
      ...stringFilter('projectPath', organizationId, eventType),
    },
    {
      id: 'lastSeen',
      header: 'Last seen',
      accessor: 'lastSeen',
      width: 180,
      renderCell: ({ row }) => <span>{formatTimestamp(row.lastSeen)}</span>,
      sortable: true,
      sortField: 'lastSeen',
      filterable: true,
      dataType: ColumnDataTypes.DATE,
      filterOperators: DATE_OPERATORS,
      filterOptions: { type: FilterOptionTypes.STATIC, options: [] },
    },
  ];
}

export interface AggregatedRepositoryColumnActions {
  /** Open the unaggregated occurrences list for a repo group. */
  onShowOccurrences: (row: AggregatedRepository) => void;
}

/**
 * Columns for the aggregated Repositories view (KIR-3807): one row per
 * repo+action+branch+project. Same shape as packages minus Vulnerabilities
 * (git access, not a package). Branch truncates so long refs don't overflow.
 */
export function createAggregatedRepositoryColumns(
  organizationId: string | undefined,
  actions: AggregatedRepositoryColumnActions
): ColumnConfig<AggregatedRepository>[] {
  const eventType: AgentCommandEventType = 'git';
  const agentFilter = stringFilter('agentName', organizationId, eventType);
  return [
    {
      id: 'repoUrl',
      header: 'Repository',
      accessor: 'repoUrl',
      width: 280,
      renderCell: ({ row }) => (
        <div className="flex items-center gap-2">
          <span className="block truncate font-medium">
            {text(row.repoUrl)}
          </span>
          {row.knownBad && (
            <Badge variant="destructive" className="uppercase">
              Known bad
            </Badge>
          )}
        </div>
      ),
      sortable: true,
      sortField: 'repoUrl',
      ...stringFilter('repoUrl', organizationId, eventType),
    },
    {
      id: 'action',
      header: 'Action',
      accessor: 'action',
      width: 100,
      renderCell: ({ row }) => <span>{text(row.action)}</span>,
      sortable: true,
      sortField: 'action',
      ...stringFilter('action', organizationId, eventType),
    },
    {
      id: 'branch',
      header: 'Branch',
      accessor: 'branch',
      width: 160,
      // Truncate long branch names so they don't overflow into the next column.
      renderCell: ({ row }) => (
        <span className="block truncate">{text(row.branch)}</span>
      ),
      sortable: true,
      sortField: 'branch',
      ...stringFilter('branch', organizationId, eventType),
    },
    {
      id: 'agents',
      header: 'Agent',
      accessor: 'agents',
      width: 180,
      renderCell: ({ row }) => <AgentsCell agents={row.agents} />,
      filterable: true,
      dataType: ColumnDataTypes.ENUM,
      filterOperators: [
        { value: FilterOperators.INCLUDES, label: 'Includes' },
        { value: FilterOperators.EXCLUDES, label: 'Excludes' },
      ],
      filterOptions: {
        ...agentFilter.filterOptions,
        render: (option: FilterOption, isSelected: boolean) => (
          <AgentFilterOption value={option.value} isSelected={isSelected} />
        ),
      },
    },
    {
      id: 'userCount',
      header: 'Users',
      accessor: 'userCount',
      width: 100,
      renderCell: ({ row }) => <span>{row.userCount}</span>,
      sortable: true,
      sortField: 'userCount',
      filterable: true,
      dataType: ColumnDataTypes.NUMBER,
      filterOperators: NUMBER_OPERATORS,
      filterOptions: { type: FilterOptionTypes.STATIC, options: [] },
      range: { min: 1, max: 100, step: 1 },
    },
    {
      id: 'occurrenceCount',
      header: 'Occurrences',
      accessor: 'occurrenceCount',
      width: 150,
      renderCell: ({ row }) => (
        <CountButton
          count={row.occurrenceCount}
          label={`Show ${row.occurrenceCount} occurrences of ${row.repoUrl ?? 'repository'}`}
          onClick={() => actions.onShowOccurrences(row)}
        />
      ),
      sortable: true,
      sortField: 'occurrenceCount',
      filterable: true,
      dataType: ColumnDataTypes.NUMBER,
      filterOperators: NUMBER_OPERATORS,
      filterOptions: { type: FilterOptionTypes.STATIC, options: [] },
      range: { min: 1, max: 100, step: 1 },
    },
    {
      id: 'projectPath',
      header: 'Project',
      accessor: 'projectPath',
      width: 180,
      renderCell: ({ row }) => (
        <ProjectCell row={row} organizationId={organizationId} />
      ),
      sortable: true,
      sortField: 'projectPath',
      ...stringFilter('projectPath', organizationId, eventType),
    },
    {
      id: 'lastSeen',
      header: 'Last seen',
      accessor: 'lastSeen',
      width: 180,
      renderCell: ({ row }) => <span>{formatTimestamp(row.lastSeen)}</span>,
      sortable: true,
      sortField: 'lastSeen',
      filterable: true,
      dataType: ColumnDataTypes.DATE,
      filterOperators: DATE_OPERATORS,
      filterOptions: { type: FilterOptionTypes.STATIC, options: [] },
    },
  ];
}
